import { useLanguage } from "@/contexts/LanguageContext";
import { fadeInUp, staggerContainer, DURATION, EASE } from "@/lib/animations";
import { motion, useInView, useSpring, useTransform } from "framer-motion";
import { Brain, Server, Smartphone, Users, Award, Clock, CheckCircle } from "lucide-react";
import { useRef, useEffect, useState } from "react";

// Stats data
const stats = [
  { value: 50, suffix: "+", label: { en: "Projects Delivered", ar: "مشروع منجز" }, icon: CheckCircle },
  { value: 100, suffix: "%", label: { en: "Client Satisfaction", ar: "رضا العملاء" }, icon: Award },
  { value: 5, suffix: "+", label: { en: "Years Experience", ar: "سنوات خبرة" }, icon: Clock },
  { value: 10, suffix: "+", label: { en: "Team Members", ar: "أعضاء الفريق" }, icon: Users },
];

// Features data
const features = [
  {
    icon: Smartphone,
    title: { en: "Cross-Platform Excellence", ar: "التميز عبر المنصات" },
    description: {
      en: "With us, you will turn your idea into a cross-platform app that works on Android, iOS, and all desktop platforms, all with one code and using the latest technologies.",
      ar: "معانا هتحول فكرتك لتطبيق متعدد المنصات يشتغل على Android و iOS و الديسكتوب بجميع منصاته كل دة بكود واحد وباستخدام احدث التقنيات."
    },
  },
  {
    icon: Server,
    title: { en: "Robust Backend Solutions", ar: "متخصصون وخبراء" },
    description: {
      en: "Our team is specialized and experienced in building smart software and systems to manage and develop all your businesses.",
      ar: "فريقنا متخصصون وخبراء فى بناء البرمجيات والانظمة الذكية لادارة كافة أعمالك و تطويرها"
    },
  },
  {
    icon: Brain,
    title: { en: "AI-Powered Innovation", ar: "الابتكار جزء من هويتنا" },
    description: {
      en: "Expanding into artificial intelligence to deliver next-generation digital solutions.",
      ar: "بنقدملك حلول مبتكرة وابداعية تناسب كافة احتياجات عملك لنساعدك فى حل مشاكلك ونمو مشروعك."
    },
  },
];

// Animated counter component
function AnimatedCounter({ value, suffix = "" }: { value: number; suffix?: string }) {
  const ref = useRef<HTMLSpanElement>(null);
  const isInView = useInView(ref, { once: true, margin: "-50px" });
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    if (isInView) {
      const duration = 2000;
      const startTime = Date.now();
      const animate = () => {
        const elapsed = Date.now() - startTime;
        const progress = Math.min(elapsed / duration, 1);
        const eased = 1 - Math.pow(1 - progress, 3); // Ease out cubic
        setDisplayValue(Math.floor(eased * value));
        if (progress < 1) {
          requestAnimationFrame(animate);
        }
      };
      requestAnimationFrame(animate);
    }
  }, [isInView, value]);

  return (
    <span ref={ref} className="tabular-nums">
      {displayValue}{suffix}
    </span>
  );
}

export default function About() {
  const { language, t } = useLanguage();
  const isRTL = language === 'ar';

  return (
    <section id="about" className="py-24 bg-muted/20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: DURATION.slow }}
          viewport={{ once: true }}
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6">
            {isRTL ? (
              <>حول <span className="gradient-text">ابتكاري</span></>
            ) : (
              <>About <span className="gradient-text">Ibtkary</span></>
            )}
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto leading-relaxed">
            {isRTL
              ? "في ابتكاري، نصنع من الافكار حلولًا رقمية تجمع بين الإبداع والتكنولوجيا."
              : "At Ibtkary, we create digital solutions from ideas that combine creativity and technology."}
          </p>
        </motion.div>

        {/* Stats Section */}
        {/* <motion.div
          className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-20"
          variants={staggerContainer}
          initial="initial"
          whileInView="animate"
          viewport={{ once: true }}
        >
          {stats.map((stat, index) => (
            <motion.div
              key={stat.label.en}
              variants={fadeInUp}
              className="text-center p-6 rounded-2xl bg-card border border-border/50 card-hover"
            >
              <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-primary/10 text-primary mb-4">
                <stat.icon className="w-6 h-6" />
              </div>
              <div className="text-3xl md:text-4xl font-bold text-foreground mb-2">
                <AnimatedCounter value={stat.value} suffix={stat.suffix} />
              </div>
              <div className="text-sm text-muted-foreground">
                {stat.label[language]}
              </div>
            </motion.div>
          ))}
        </motion.div> */}

        {/* Main Content Grid */}
        <div className={`grid lg:grid-cols-2 gap-12 lg:gap-16 items-center ${isRTL ? 'dir-rtl' : 'dir-ltr'}`}>
          {/* Image Section */}
          <motion.div
            initial={{ opacity: 0, x: isRTL ? 30 : -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: DURATION.slow }}
            viewport={{ once: true }}
            className="relative"
          >
            <div className="relative">
              {/* Subtle background decoration */}
              <div className="absolute -inset-4 bg-gradient-to-br from-primary/5 to-secondary/5 rounded-3xl" />
              <img
                src="/assets/about-us.png"
                alt={isRTL ? "فريق محترف يتعاون على الحلول الرقمية" : "Professional team collaborating on digital solutions"}
                className="relative rounded-2xl shadow-xl w-full h-auto"
              />
            </div>
          </motion.div>

          {/* Features Section */}
          <motion.div
            className="space-y-6"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {features.map((feature, index) => (
              <motion.div
                key={feature.title.en}
                variants={fadeInUp}
                className={`flex items-start gap-4 p-5 rounded-xl bg-card border border-border/50 card-hover ${isRTL ? 'text-right' : ''}`}
                style={{ direction: isRTL ? 'rtl' : 'ltr' }}
              >
                {/* Icon */}
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-primary/10 flex items-center justify-center">
                  <feature.icon className="w-6 h-6 text-primary" />
                </div>

                {/* Text content */}
                <div className="flex-1">
                  <h3 className="text-lg font-semibold mb-2 text-foreground">
                    {feature.title[language]}
                  </h3>
                  <p className="text-muted-foreground leading-relaxed text-sm">
                    {feature.description[language]}
                  </p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
}
